/**
 * InsufficientTokensModal — shown when an export returns HTTP 402.
 *
 * Two flavors of message:
 *   * If the user used to have a balance and burned through it this month,
 *     we frame it as "se te acabaron los fierros del mes" and remind them
 *     the next batch of 15 lands automatically.
 *   * If they've never had a balance (edge case), we explain the model.
 *
 * Two clear CTAs:
 *   * Comprar fierros (one-time pack).
 *   * Suscribirse (recurring, unlimited).
 *
 * Triggered globally by the axios interceptor (`lib/api.ts`) writing the
 * 402 payload into the `billingStore`.
 */

import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Infinity as InfinityIcon,
  Sparkles,
  CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { type Plan, formatPrice } from '@/features/billing/types';
import { useBuyPlan } from '@/features/billing/useBuyPlan';
import { api } from '@/lib/api';
import { useBillingStore } from '@/stores/billingStore';

export function InsufficientTokensModal() {
  const detail = useBillingStore((s) => s.insufficient);
  const close = useBillingStore((s) => s.closeInsufficient);
  const navigate = useNavigate();
  const buyPlan = useBuyPlan();

  // Pre-fetch the catalog so the in-modal CTAs can buy without an extra
  // round-trip. The query is cached, so this is virtually free if the
  // user already visited /app/billing.
  const plansQuery = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => (await api.get<Plan[]>('/api/billing/plans')).data,
    enabled: !!detail,
  });
  const plans = plansQuery.data ?? [];
  const pack50 = plans.find((p) => p.slug === 'pack_50');
  const subMonth = plans.find((p) => p.slug === 'sub_month');

  const open = !!detail;
  const cost = detail?.cost ?? 0;
  const balance = detail?.balance ?? 0;
  const missing = Math.max(cost - balance, 0);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.80)', backdropFilter: 'blur(10px)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{
            background: 'var(--surface-1)',
            borderColor: 'var(--accent)',
            boxShadow: '0 30px 80px rgba(245,158,11,0.25), 0 0 0 1px var(--accent)',
          }}
        >
          <Dialog.Close
            className="absolute right-3 top-3 z-10 rounded-[var(--radius-sm)] p-1.5 transition-colors hover:bg-[var(--surface-2)]"
            aria-label="Cerrar"
          >
            <X size={16} strokeWidth={1.8} />
          </Dialog.Close>

          {/* Hero band */}
          <div
            className="relative px-6 pb-5 pt-7"
            style={{
              background:
                'linear-gradient(135deg, rgba(245,158,11,0.22), rgba(245,158,11,0.04) 70%)',
            }}
          >
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-2.5 py-1 text-[10px] uppercase tracking-[0.18em]"
              style={{ background: 'var(--surface-1)', borderColor: 'var(--accent)', color: 'var(--accent)' }}
            >
              <Sparkles size={10} strokeWidth={2} />
              Saldo agotado
            </div>
            <Dialog.Title
              className="text-2xl md:text-3xl"
              style={{ fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 }}
            >
              Se te acabaron los <span style={{ color: 'var(--accent)' }}>fierros</span> del mes
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-[13.5px]" style={{ color: 'var(--text-secondary)' }}>
              Esta exportación cuesta <strong>{cost}</strong> {cost === 1 ? 'fierro' : 'fierros'} y
              tienes <strong>{balance}</strong>. Te faltan <strong>{missing}</strong>.
            </Dialog.Description>
          </div>

          {/* Refill reassurance */}
          <div className="border-y px-6 py-3" style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-2)' }}>
            <div className="flex items-center gap-2.5 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
              <CalendarClock size={14} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
              El próximo mes te regalamos <strong>otros 15 fierros</strong> automáticamente. Si los
              necesitas ahora, escoge una opción:
            </div>
          </div>

          {/* Two CTAs side by side */}
          <div className="grid grid-cols-1 gap-3 p-6 sm:grid-cols-2">
            {/* Buy fierros */}
            <CtaCard
              tag="Pago único"
              icon={<FierroIcon size={20} />}
              title="50 fierros"
              price={pack50 ? formatPrice(pack50.price_cents) : 'S/. 14.90'}
              sub="≈ 25 exportaciones más"
              onClick={async () => {
                if (!pack50) return;
                const r = await buyPlan.buy(pack50);
                if (r) close();
              }}
              busy={pack50 ? buyPlan.busyPlanId === pack50.id : false}
            />
            {/* Subscribe */}
            <CtaCard
              tag="Suscripción"
              icon={<InfinityIcon size={20} strokeWidth={2} />}
              title="Ilimitado / mes"
              price={subMonth ? formatPrice(subMonth.price_cents) : 'S/. 39.00'}
              sub="exportaciones sin límite"
              featured
              onClick={async () => {
                if (!subMonth) return;
                const r = await buyPlan.buy(subMonth);
                if (r) close();
              }}
              busy={subMonth ? buyPlan.busyPlanId === subMonth.id : false}
            />
          </div>

          {/* Footer with secondary actions */}
          <div className="flex items-center justify-between border-t px-6 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
            <button
              type="button"
              onClick={close}
              className="text-[12px] underline-offset-2 hover:underline"
              style={{ color: 'var(--text-muted)' }}
            >
              Esperar al próximo mes
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                close();
                navigate('/app/billing');
              }}
            >
              Ver todos los planes
            </Button>
          </div>

          {buyPlan.error && (
            <div
              className="border-t p-3 text-center text-[12.5px]"
              style={{
                borderColor: 'var(--border-subtle)',
                background: 'rgba(220,38,38,0.06)',
                color: '#fca5a5',
              }}
            >
              {buyPlan.error}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CtaCard({
  tag,
  icon,
  title,
  price,
  sub,
  onClick,
  busy,
  featured,
}: {
  tag: string;
  icon: React.ReactNode;
  title: string;
  price: string;
  sub: string;
  onClick: () => void;
  busy: boolean;
  featured?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="group relative flex flex-col rounded-[var(--radius-md)] border p-4 text-left transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
      style={{
        background: featured ? 'rgba(245,158,11,0.10)' : 'var(--surface-2)',
        borderColor: featured ? 'var(--accent)' : 'var(--border-default)',
      }}
    >
      {featured && (
        <span
          className="absolute -top-2 right-3 rounded-[var(--radius-pill)] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          Recomendado
        </span>
      )}
      <div className="mb-2 flex items-center gap-2">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          {icon}
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
          {tag}
        </span>
      </div>
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {title}
      </div>
      <div className="mt-0.5 font-mono tabular text-xl" style={{ color: 'var(--text-primary)' }}>
        {busy ? 'Procesando…' : price}
      </div>
      <div className="mt-0.5 text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
        {sub}
      </div>
    </button>
  );
}
